import { createNode } from "@/utils/flowNode";
import { addNodeId } from "@/utils/nodeIdPool";
import cloneDeep from "lodash/cloneDeep";
import { bindWechatFlowTaskA, getSceneHitStatA } from "@/server/api";
import { collectWechatFlowTaskIds } from "@/utils/wechatFlowTask";

const store = {
  namespaced: true,
  state: {
    flows: [],
    curFlowId: 0, // 当前flow id
    oldFlowsString: "[]", // 用于判断用户是否修改过
    hitStats: {}, // { [flowId]: { percent, totalHit, nodeStats: { [nodeId]: { percent, nodeHit } } } }
  },
  getters: {
    flowMap: (state) => {
      return state.flows?.reduce((flowMap, next) => {
        flowMap[next.id] = next;
        return flowMap;
      }, {});
    },
    nodes: (state) => {
      const curFlow = state.flows.find((flow) => flow.id === state.curFlowId);
      return curFlow?.nodes || [];
    },
    flowHitStat: (state) => (flowId) => state.hitStats[flowId],
    nodeHitStat: (state) => (flowId, nodeId) =>
      state.hitStats[flowId]?.nodeStats?.[nodeId],
  },
  mutations: {
    // 切换流程
    SWITCH_FLOW(state, flowId) {
      state.curFlowId = flowId;
    },
    // 设置流程
    SET_FLOWS(state, flows) {
      state.oldFlowsString = JSON.stringify(flows);
      const newFlows = flows.map((flow) => {
        flow.nodes = flow.nodes.filter(Boolean).map((node) => {
          if (node.type !== "condition") return node;
          node.outs = node.condition_outs;
          delete node.condition_outs;
          return node;
        });
        return flow;
      });
      state.flows = newFlows;
    },
    SAVE_OLD_FLOWS(state, flows) {
      state.oldFlowsString = JSON.stringify(flows);
    },
    // 修改flows
    SET_FLOW_NAME(state, flow) {
      const flowIndex = state.flows.findIndex((item) => item.id === flow.id);
      if (flowIndex < 0) return;
      state.flows[flowIndex].name = flow.name;
    },
    // 添加flows
    ADD_FLOW(state, flow) {
      state.flows.push(flow);
    },
    // 删除flows
    DEL_FLOW(state, flowId) {
      const flowIndex = state.flows.findIndex((item) => item.id === flowId);
      if (flowIndex < 0) return;
      state.flows.splice(flowIndex, 1);
    },
    // 修改node
    SET_FLOW_NODE(state, newNode) {
      const curFlow = state.flows.find((flow) => flow.id === state.curFlowId);
      if (!curFlow) return;
      const nodeIndex = curFlow.nodes?.findIndex(
        (node) => node.id === newNode.id,
      );
      if (nodeIndex >= 0) {
        curFlow.nodes.splice(nodeIndex, 1, newNode);
      }
    },
    // 添加node
    AND_FLOW_NODE(state, node) {
      const curFlow = state.flows.find((flow) => flow.id === state.curFlowId);
      if (!curFlow) return;
      curFlow.nodes?.push(node);
    },
    DEL_FLOW_NODE(state, nodeId) {
      const curFlow = state.flows.find((flow) => flow.id === state.curFlowId);
      if (!curFlow) return;
      const nodeIndex = curFlow.nodes?.findIndex((node) => node.id === nodeId);
      curFlow.nodes.splice(nodeIndex, 1);
    },
    SET_START_NODE(state, nodeId) {
      const curFlow = state.flows.find((flow) => flow.id === state.curFlowId);
      if (!curFlow) return;
      const nodeIndex = curFlow.nodes?.findIndex((node) => node.id === nodeId);
      curFlow.nodes.unshift(...curFlow.nodes.splice(nodeIndex, 1));
    },
    // 修改连线
    SET_CONNECTION(state, { sourceId, targetId, nodeId }) {
      const curFlow = state.flows.find((flow) => flow.id === state.curFlowId);
      const curNode = curFlow.nodes.find((node) => {
        return node.id === nodeId;
      });
      if (!curNode) return;
      const curOutIndex = curNode.outs.findIndex((out) => out.id === sourceId);
      if (curOutIndex < 0) return;
      curNode.outs.splice(curOutIndex, 1, {
        ...curNode.outs[curOutIndex],
        target_id: targetId,
      });
    },
    SET_HIT_STATS(state, hitStats) {
      state.hitStats = hitStats;
    },
    CLEAR_TARGET_ID(state, targetId) {
      const curFlow = state.flows.find((flow) => flow.id === state.curFlowId);
      if (!curFlow) return;
      for (let i = 0; i < curFlow.nodes?.length; i++) {
        const node = curFlow.nodes[i];
        for (let j = 0; j < node.outs?.length; j++) {
          const out = node.outs[j];
          if (out.target_id === targetId) {
            curFlow.nodes[i].outs[j].target_id = "";
          }
        }
      }
    },
  },
  actions: {
    /**
     * 节点相关操作
     */
    setFlows({ commit, dispatch, state }, flows = []) {
      // 如果用户修改了但是没保存则不执行
      if (JSON.stringify(state.flows) !== state.oldFlowsString) return;
      commit("SET_FLOWS", flows);
      if (state.curFlowId === 0) {
        const firstFlowId = flows[0]?.id || 0;
        dispatch("switchFlow", firstFlowId);
      }
    },
    // 编辑节点 返回新的节点数据
    editNode({ commit }, node = {}) {
      // 有id就修改数据，没有id添加
      if (node.id) {
        commit("SET_FLOW_NODE", node);
        return node;
      }
      const newNode = createNode(node);
      commit("AND_FLOW_NODE", newNode);
      return newNode;
    },
    // 设置连线
    setConnection({ commit }, out) {
      commit("SET_CONNECTION", {
        sourceId: out.sourceId,
        targetId: out.targetId,
        nodeId: out.nodeId,
      });
    },
    // 修改节点坐标
    setNodePos({ commit, getters }, { id, x, y }) {
      // 获取当前node节点
      const curNode = getters.nodes.find((node) => node.id === id);
      if (!curNode) return;
      commit("SET_FLOW_NODE", { ...curNode, top: y, left: x });
    },
    // 删除节点
    delFlowNode({ commit }, id) {
      commit("DEL_FLOW_NODE", id);
      commit("CLEAR_TARGET_ID", id);
    },
    // 设置开始节点
    setStartNode({ commit }, id) {
      commit("SET_START_NODE", id);
    },
    /**
     * 流程相关操作
     */
    // 切换流程
    switchFlow({ commit, getters }, flowId) {
      commit("SWITCH_FLOW", flowId);
    },
    // 编辑流程
    editFlow({ commit }, flow = {}) {
      if (flow.id) {
        commit("SET_FLOW_NAME", flow);
        return flow;
      }
      flow.id = addNodeId("F");
      flow.nodes = [];
      commit("ADD_FLOW", flow);
      return flow;
    },
    // 删除流程
    delFlow({ commit, state, dispatch }, flowId) {
      // 修改所有跳转节点 action_data 为当前flowId
      for (let i = 0; i < state.flows?.length; i++) {
        const nodes = state.flows[i].nodes || [];
        for (let j = 0; j < nodes.length; j++) {
          const node = nodes[j];
          if (node.action_data === flowId) {
            node.action_data = "";
            dispatch("editNode", node);
          }
        }
      }
      commit("DEL_FLOW", flowId);
    },
    // 拉取话术命中统计
    fetchHitStats({ commit, rootState }, params = {}) {
      const sceneId = rootState.scene.sceneId;
      if (!sceneId) return Promise.resolve();
      return getSceneHitStatA({
        flowId: sceneId,
        ...params,
      })
        .then((res) => {
          if (res?.code !== 200) return;
          const map = {};
          (res.data || []).forEach((item) => {
            const nodeStats = {};
            (item.nodeList || []).forEach((n) => {
              nodeStats[n.nodeId] = { percent: n.percent, nodeHit: n.nodeHit };
            });
            map[item.sceneId] = {
              percent: item.percent,
              totalHit: item.totalHit,
              nodeStats,
            };
          });
          commit("SET_HIT_STATS", map);
        })
        .catch(() => {});
    },
    submitFlow({ commit, dispatch, state }) {
      let flows = cloneDeep(state.flows);
      flows = flows?.map((flow) => {
        flow.nodes = flow.nodes.map((node) => {
          if (node.type !== "condition") return node;
          node.condition_outs = node.outs;
          delete node.outs;
          return node;
        });
        return flow;
      });
      commit("SAVE_OLD_FLOWS", flows);
      return dispatch(
        "scene/savePortionScene",
        { key: "scenes", data: flows },
        { root: true },
      ).then((res) => {
        dispatch("reportWechatFlowTasks").catch(() => {});
        return res;
      });
    },
    reportWechatFlowTasks({ rootState }) {
      const flowId = rootState.scene && rootState.scene.sceneId;
      if (!flowId) return Promise.resolve();
      const taskIds = collectWechatFlowTaskIds(
        rootState.setting && rootState.setting.apiSettings,
      );
      if (!taskIds.length) return Promise.resolve();
      return Promise.all(
        taskIds.map((task_id) =>
          bindWechatFlowTaskA({
            flow_id: flowId,
            task_id,
          }).catch(() => {}),
        ),
      );
    },
  },
};
export default store;
